import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { consumePostAuthRedirect } from '@/lib/postAuthRedirect';
import { trackPixel } from '@/lib/metaPixel';
import { trackAdsConversion } from '@/lib/googleAds';
import { track } from '@/lib/analytics';
import AuthProviders from '@/components/feature/AuthProviders';
import { Logo } from '@/components/feature/Logo';
import { MeshBg } from '@/components/feature/MeshBg';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { signUp, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Signed in. Unverified email/password users go to the code-verify screen
    // (it auto-sends the 6-digit code on mount); verified users into the app.
    // AuthGate enforces the same gate server-side, so this is just the direct route.
    if (user.email && !user.emailVerified) {
      // Leave any post-auth checkout stash intact — the verify-email screen
      // consumes it once the email is verified.
      navigate('/auth/verify-email', { replace: true });
    } else {
      // Already verified (e.g. Google sign-up). Resume a stashed checkout if
      // they came from "Start Free Trial"; otherwise land on /brands/new — the
      // free-tier onboarding (they get 20 credits on signup, no card).
      navigate(consumePostAuthRedirect() ?? '/brands/new', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await signUp(email, password, displayName);
    setLoading(false);
    if (result.error) {
      setError(result.error);
    } else {
      // New account created → fire ad-platform conversions for optimization.
      trackPixel('CompleteRegistration', { method: 'email' });
      trackAdsConversion('signup'); // Google Ads Sign-up conversion
      track('signup_completed', { method: 'email' });
    }
    // On success the user is now signed in; the effect above routes them to the
    // verify-email code screen (unverified) — no "check your inbox" interstitial.
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-surface flex items-center justify-center p-4">
      <MeshBg />
      <div className="relative z-10 w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <a href="/" className="inline-flex items-center mb-4">
            <Logo height={73} />
          </a>
          <p className="text-dark-muted text-sm">Create your free account</p>
        </div>

        {/* Card */}
        <div className="gradient-stroke bg-white rounded-2xl p-8 shadow-2xl shadow-primary/45">
          {error && (
            <div className="mb-4 flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 text-red-700 text-sm font-medium border border-red-200">
              <i className="ri-error-warning-line" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="displayName" className="block text-sm font-semibold text-dark mb-2">
                Display Name <span className="text-dark-muted font-normal">(optional)</span>
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Jane Doe"
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-dark mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full px-4 py-3 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-dark mb-2">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Min. 6 characters"
                  required
                  minLength={6}
                  className="w-full px-4 py-3 pr-12 rounded-xl border border-surface-dark/60 bg-white text-dark text-sm placeholder:text-dark-muted/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-dark-muted hover:text-dark transition-colors cursor-pointer"
                >
                  <i className={showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-primary hover:bg-primary-hover text-white text-sm font-bold transition-all cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="w-5 h-5 rounded-full border-2 border-white/30 border-t-white animate-spin" />
              ) : (
                <>
                  <i className="ri-user-add-line" />
                  Create Account
                </>
              )}
            </button>
          </form>

          {/* Google + Phone */}
          <AuthProviders />
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-dark-muted mt-6">
          Already have an account?{' '}
          <a
            href="/auth/sign-in"
            className="font-semibold text-primary hover:text-primary-hover transition-colors"
          >
            Sign in
          </a>
        </p>
      </div>
    </div>
  );
}